import React from 'react';
import { CreativePreview } from 'components/CreativePreview/CreativePreview';
import { DefaultCreativePreviewModel } from 'components/CreativePreview/CreativePreviewModel';
import { faPencilAlt, faEye, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import styles from './creativeList.module.scss';
import i18n from 'i18next';
import _ from 'lodash';
import { CreativeListModel } from './CreativeListModel';
import { Link } from 'react-router-dom';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { hasFuncs, inCondition } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import TableRowToolBar from 'containers/TableRowToolBar/TableRowToolBar';
import { Status } from 'components/Status/Status';
import { CreativeReviews } from 'components/CreativeReview/CreativeReviews';
import { BatchCreativeReviews } from 'components/CreativeReview/BatchCreativeReviews';
import { ProductAvailability } from 'core/product/Product';
import { CreativeRelatedProductInfo, CreativeStatus, CreativeType, parseCreativeValue } from 'core/creative/Creative';
import { RichMediaDeliveryPosition } from 'containers/Creatives/CreativeSetupFlow/FlowSteps/SubSteps/FormContent/RichMediaFormModel';

// 站外投遞的 Rich Media 商品是非必填，沒綁商品時「庫存」這件事對它不適用。
export const isProductOptionalCreative = (creative: any): boolean =>
  _.get(creative, 'creativeType') === CreativeType.RICH_MEDIA &&
  _.get(parseCreativeValue(creative), 'deliveryPosition') === RichMediaDeliveryPosition.OFF_SITE;

// 「顯示商品資訊」的 modal 直接吃 row 上的 relatedProductInfo，所以沒有商品就沒有東西可看。
// Rich Media 的商品在站外是非必填（站內必填），故只有它需要看有沒有綁商品；
// 其他素材類型維持原本「依素材類型決定」的行為，不受商品有無影響。
export const canShowRelatedProducts = (creative: any): boolean => {
  const creativeType = _.get(creative, 'creativeType');
  if ([
    CreativeType.PRODUCT_NATIVE,
    CreativeType.SITE_WIDE_RECOMMENDED,
    CreativeType.PERSONALIZED_GOLDEN_RECOMMENDED
  ].includes(creativeType)) {
    return false;
  }
  if (creativeType === CreativeType.RICH_MEDIA) {
    return !_.isEmpty(_.get(creative, 'relatedProductInfo'));
  }
  return true;
};

export const approvalFormatter = (
  creativeData: any,
  showCreativeType: boolean,
  showAdx: boolean,
  reviewCreatives: (adx: string, creativeIds: number[]) => void
) => {
  if (!creativeData) {
    return <div />;
  }

  return (
    <CreativeReviews
      showCreativeType={showCreativeType}
      creativeData={creativeData}
      showAdx={showAdx}
      reviewCreatives={reviewCreatives}
    />
  );
};

export const availabilityFormatter = (
  creativeRelatedProductInfo: CreativeRelatedProductInfo[],
  productOptional: boolean = false
) => {
  if (!creativeRelatedProductInfo) {
    return 'N/A';
  }

  const activeProducts = creativeRelatedProductInfo.filter(
    (item) => item.state !== CreativeStatus.DEACTIVE
  );

  // 商品非必填的素材沒綁商品時不該顯示「無庫存」——它不是缺貨，是沒有商品可談庫存。
  if (productOptional && activeProducts.length === 0) {
    return 'N/A';
  }

  const availabilityMap = activeProducts.reduce((acc, group) => {
    if (!group.availability) {
      return acc;
    }
    acc[group.availability] = (acc[group.availability] || 0) + 1;
    return acc;
  }, {
    [ProductAvailability.IN_STOCK]: 0,
    [ProductAvailability.OUT_OF_STOCK]: 0
  });

  let availabilityText = i18n.t<string>('product.status.inStock');
  let availabilityColor = 'black';
  if (
    activeProducts.length === 0 ||
    availabilityMap[ProductAvailability.OUT_OF_STOCK] === activeProducts.length
  ) {
    availabilityText = i18n.t<string>('product.status.outOfStock');
    availabilityColor = 'black';
  } else if (
    availabilityMap[ProductAvailability.IN_STOCK] === activeProducts.length
  ) {
    availabilityText = i18n.t<string>('product.status.inStock');
    availabilityColor = 'success';
  } else {
    availabilityText = i18n.t<string>('product.status.partiallyOutOfStock');
    availabilityColor = 'warning';
  }

  return (
    <Status
      label={availabilityText}
      color={availabilityColor}
    />
  );
};

export const batchApprovalFormatter = (relatedProductInfo: CreativeRelatedProductInfo[], reviewCreatives: (adx: string, creativeIds: number[]) => void) => {
  if (!relatedProductInfo) {
    return 'N/A';
  }

  return (
    <BatchCreativeReviews
      creativesData={relatedProductInfo
        .filter((data) => data.state !== CreativeStatus.DEACTIVE)
        .map((data) => {
          return {
            creativeId: data.creativeId,
            approvalData: Object.values(data.approvalStatus)[0]
          };
        })}
      reviewCreatives={reviewCreatives}
    />
  );
};

export const getCreativeListFormatters = (model: CreativeListModel) => {

  const creativeWritePermission = hasFuncs(Permission.CREATIVE_WRITE);

  const nameIdFormatter = (nameIdData: any, creativeData: any, _1, formatExtraData) => {
    const renderOtherInfo = _.get(formatExtraData, 'renderOtherInfo');
    if (creativeData.id === 0) {
      return <div>{i18n.t<string>('creativeList.labels.nameIdSummary', { count: nameIdData })}</div>;
    }
    const handleOnSelect = (e) => {
      model.handleOnSelect(nameIdData.id);
    };
    const renderLabel = () => <span className={styles.creativeName}>{nameIdData.name}</span>;
    return (
      <div className={styles.creativeCell}>
        <div className={styles.selectCheckBox}>
          <PermissionChecker permissionAware={creativeWritePermission}>
            <input
              type='checkbox'
              checked={creativeData.selected}
              onChange={handleOnSelect}
              id={`creativeListInput${nameIdData.id}`}
            />
            <label htmlFor={`creativeListInput${nameIdData.id}`} />
          </PermissionChecker>
        </div>
        <div className={styles.info}>
          <PermissionChecker
            permissionAware={creativeWritePermission}
            renderOtherwise={renderLabel}
          >
            <Link
              to={`/creatives/${nameIdData.id}/edit`}
              className={styles.creativeName}
            >
              {nameIdData.name}
            </Link>
          </PermissionChecker>
          <div className={styles.creativeID}>
            {`ID: ${nameIdData.id}`}
          </div>
          {
            renderOtherInfo && renderOtherInfo(creativeData)
          }
        </div>
      </div>
    );
  };

  const productIdRenderer = (productId: any) => {
    if (!productId) {
      return <div />;
    }
    return (
      <div className={styles.creativeID}>
        {`${i18n.t<string>('creativeList.labels.productID')}: ${productId}`}
      </div>
    );
  };

  const nameIdHeaderFormatter = (nameIdHeader: any, columnIndex: number, { sortElement }) => {
    return (
      <div className={styles.nameIdHeader}>
        <div className={styles.selectCheckBox}>
          <PermissionChecker permissionAware={creativeWritePermission}>
            <input
              type='checkbox'
              checked={model.state.viewModelData.length === model.state.selectedCreatives.length}
              ref={el => el && (el.indeterminate = model.state.viewModelData.length !== model.state.selectedCreatives.length && model.state.selectedCreatives.length > 0)}
              onChange={model.handleOnSelectAll}
              id={'creativeNameIdHeaderFormatter'}
            />
            <label htmlFor='creativeNameIdHeaderFormatter' />
          </PermissionChecker>
        </div>
        {i18n.t<string>(nameIdHeader.text)}
        {sortElement}
      </div>
    );
  };

  const previewFormatter = (previewData: any, creative: any) => {
    if (!previewData) {
      return <div />;
    }
    return (
      <div className={`${styles.creativeCell} ${styles.previewCell}`}>
        <div className={styles.creativeReview}>
          <CreativePreview model={new DefaultCreativePreviewModel(previewData)} />
        </div>
        <IconWithTooltip
          placement='right'
          className={styles.detailBtn}
          onClick={model.showCreativeDetailModal.bind(model, previewData)}
          icon={faEye}
          tooltipProps={{
            id: `creativeListPreviewHint-${creative.id}`,
            tooltip: i18n.t<string>('creativeList.labels.previewHint')
          }}
        />
      </div>
    );
  };

  const textFormatter = (text: any) => {
    if (!text) {
      return <div />;
    }
    return (
      <div className={styles.creativeCell}>
        <div className={styles.text}>
          {text}
        </div>
      </div>
    );
  };

  const urlFormatter = (url: any) => {
    if (!url) {
      return <div />;
    }
    return (
      <div className={styles.creativeCell}>
        <a href={url} target='_blank' rel='noopener noreferrer'>{url}</a>
      </div>
    );
  };

  const streamingFormatter = (streamingSetting: { inStream: boolean, outStream: boolean }) => {
    if (!streamingSetting) {
      return <div />;
    }
    let content = streamingSetting.inStream ?
      i18n.t<string>('creativeList.labels.inStream') :
      i18n.t<string>('creativeList.labels.outStream');
    if (streamingSetting.inStream && streamingSetting.outStream) {
      content = i18n.t<string>('creativeList.labels.unlimited');
    }
    return (
      <div className={styles.creativeCell}>
        <span style={{ whiteSpace: 'nowrap' }}>
          {content}
        </span>
      </div>
    );
  };

  const skipSettingFormatter = (skipSetting) => {
    if (!skipSetting) {
      return <div />;
    }
    const { skippable, nonSkippable, skipOffset } = skipSetting;
    return (
      <div className={styles.creativeCell}>
        {skippable &&
          <>
            {`${i18n.t<string>('creativeList.labels.skippable')} : ${i18n.t<string>('creativeList.labels.skipOffset', { offset: skipOffset })}`}
            <br />
          </>
        }
        {nonSkippable &&
          <>
            {i18n.t<string>('creativeList.labels.nonSkippable')}
            <br />
          </>
        }
        {(!skippable && !nonSkippable) && i18n.t<string>('creativeList.labels.nonSkippable')}
      </div>
    );
  };

  const floatingEditBtnsFormatter = (cell: any, creative: any): any => {
    const canNotDeleteCreative = _.get(creative, 'bindingCampaign', []).length > 0;
    return (
      <PermissionChecker
        permissionAware={hasFuncs(Permission.CREATIVE_WRITE).or(
          hasFuncs(Permission.CREATIVE_DEL)
        )}
      >
        <TableRowToolBar className={styles.floatingEditArea}>
          <PermissionChecker permissionAware={creativeWritePermission}>
            <Link to={`/creatives/${creative.id}/edit`}>
              <IconWithTooltip
                icon={faPencilAlt}
                tooltipProps={{
                  id: `creativeListEditTip-${creative.id}`,
                  tooltip: i18n.t<string>('creativeList.labels.editHint')
                }}
              />
            </Link>
            <IconWithTooltip
              icon={faCopy}
              onClick={model.cloneCreatives.bind(model, [creative.id])}
              tooltipProps={{
                id: `creativeListCloneTip-${creative.id}`,
                tooltip: i18n.t<string>('creativeList.labels.cloneHint')
              }}
            />
          </PermissionChecker>
          <PermissionChecker
            permissionAware={hasFuncs(Permission.CREATIVE_DEL)}
          >
            <IconWithTooltip
              disabled={canNotDeleteCreative}
              icon={faTrashAlt}
              onClick={model.showDeleteCreativeModal.bind(model, [creative.id])}
              tooltipProps={{
                id: `creativeListDeleteTip-${creative.id}`,
                tooltip: canNotDeleteCreative
                  ? i18n.t<string>('creativeList.labels.cannotDeleteHint')
                  : i18n.t<string>('creativeList.labels.deleteHint')
              }}
            />
          </PermissionChecker>
          <PermissionChecker
            permissionAware={hasFuncs(Permission.CREATIVE_READ).and(
              inCondition(() => canShowRelatedProducts(creative))
            )}
          >
            <IconWithTooltip
              icon={faEye}
              onClick={model.showCreativeRelatedProductListModal.bind(model, creative)}
              tooltipProps={{
                id: `creativeListCreativeRelatedProductTip-${creative.id}`,
                tooltip: i18n.t<string>('creativeList.labels.showCreativeRelatedProductsHint')
              }}
            />
          </PermissionChecker>
        </TableRowToolBar>
      </PermissionChecker>
    );
  };

  const stateFormatter = status => {
    return (
      <Status
        label={status ?
          i18n.t<string>('campaignCreativeList.status.enable') :
          i18n.t<string>('campaignCreativeList.status.disable')}
        color={status ? 'success' : 'black'}
      />
    );
  };

  const productNativeStateFormatter = (status, creative) => {
    const stockAvailable = creative.stockAvailable;
    let label = status ?
      i18n.t<string>('campaignCreativeList.status.enable') :
      i18n.t<string>('campaignCreativeList.status.disable');
    let color = status ? 'success' : 'black';
    if (!stockAvailable) {
      label = i18n.t<string>('campaignCreativeList.status.outOfStock');
      color = 'black';
    }
    return (
      <Status
        label={label}
        color={color}
      />
    );
  };

  return {
    nameIdFormatter,
    previewFormatter,
    textFormatter,
    urlFormatter,
    approvalFormatter: (
      creativeData: any,
      showCreativeType: boolean = true,
      showAdx: boolean = true
    ) =>
      approvalFormatter(
        creativeData,
        showCreativeType,
        showAdx,
        model.reviewCreatives.bind(model)
      ),
    floatingEditBtnsFormatter,
    nameIdHeaderFormatter,
    streamingFormatter,
    skipSettingFormatter,
    stateFormatter,
    productNativeStateFormatter,
    productIdRenderer,
    batchApprovalFormatter: (relatedProductInfo: CreativeRelatedProductInfo[]) =>
      batchApprovalFormatter(
        relatedProductInfo,
        model.reviewCreatives.bind(model)
      ),
    availabilityFormatter
  };
};
